import { createReducer, createActions } from 'reduxsauce';
import Immutable from 'seamless-immutable';
import qs from 'qs';

import constants, { CATEGORY_FILTER_SORT_ORDER } from 'utils/constants';
import { features } from 'utils/features';

const DEFAULT_INTERCALATION_ROWS = 12;
const DEFAULT_BANNER_GROUP = 6;

const { Types, Creators } = createActions({
  getCourses: ['lat', 'lon', 'hierarchizedUrl', 'params'],
  getCoursesDone: ['courses', 'filterContent'],
  getCoursesError: ['message'],
  getSimilarCourses: ['courseId', 'params'],
  getSimilarCoursesDone: ['course', 'similarCourses'],
  getSimilarCoursesError: ['message'],
  getNextPage: [],
  getSectionCourses: ['lat', 'lon', 'params'],
  getSectionCoursesDone: ['courseSection', 'courses'],
  getSectionCoursesError: ['message'],
  setViewMode: ['viewMode'],
  setCourseBanners: ['banners'],
  toggleCourseFavorite: ['favoriteCourseId'],
});

export const SearchCoursesTypes = Types;
export default Creators;

export const INITIAL_STATE = Immutable.from({
  course: null,
  courses: [],
  filterContent: {},
  similarCourses: [],
  banners: [],
  errorMessage: '',
  hasMore: true,
  isDone: false,
  isError: false,
  isLoading: false,
  pageNumber: 1,
  courseSection: null,
  viewMode: 'list',
});

const compareEntities = (first, second, isDescendingOrder = false) => {
  let comparison = 0;
  if (first < second) comparison = -1;
  if (first > second) comparison = 1;

  return isDescendingOrder ? -comparison : comparison;
};

const sortByCategoryAndFilters = (courseA, courseB, sortKeys, category) => {
  if (!category) {
    const { key, decending } = sortKeys.find((item) => item.default);

    return compareEntities(courseA[key], courseB[key], decending);
  }
  // eslint-disable-next-line no-restricted-syntax
  for (const { key, decending } of sortKeys) {
    // eslint-disable-next-line no-continue
    if (courseA[key] === null && courseB[key] === null) continue;
    if (courseA[key] === null) return 1;
    if (courseB[key] === null) return -1;
    if (courseA[key] !== null && courseB[key] !== null) {
      const c = compareEntities(courseA[key], courseB[key], decending);
      // eslint-disable-next-line no-continue
      if (c === 0) continue;
      return c;
    }
  }
};

const sortCourses = (courses, sortBy) => {
  const { categoryFilter } = qs.parse(window.location.search, { ignoreQueryPrefix: true });
  const primarySortKeys = CATEGORY_FILTER_SORT_ORDER[categoryFilter] || [];
  const defaultSortKey = { key: 'distance', decending: false, default: true };

  return [...courses].sort((
    { course: entityA },
    { course: entityB },
  ) => {
    let value;
    let courseA = { ...entityA } || {};
    let courseB = { ...entityB } || {};

    switch (sortBy) {
      case 'distance':
      case 'best-match':
        defaultSortKey.key = 'distance';
        defaultSortKey.decending = false;
        break;
      case 'price-lowest-first':
        courseA = { ...courseA, minRegularRate: Number(courseA.minRegularRate) || Infinity };
        courseB = { ...courseB, minRegularRate: Number(courseB.minRegularRate) || Infinity };
        defaultSortKey.key = 'minRegularRate';
        defaultSortKey.decending = false;
        break;
      case 'name-asc':
      case 'name-dsc':
        courseA = { ...courseA, name: courseA.name.toLowerCase() };
        courseB = { ...courseB, name: courseB.name.toLowerCase() };
        defaultSortKey.key = 'name';
        defaultSortKey.decending = sortBy === 'name-dsc';
        break;
      case 'rating':
        defaultSortKey.key = 'ratingValue';
        defaultSortKey.decending = true;
        break;
      default:
        value = 0; break;
    }

    courseA.ratingRounded = courseA.rating?.valueRounded || 0;
    courseB.ratingRounded = courseB.rating?.valueRounded || 0;
    courseA.ratingValue = courseA.rating?.value || 0;
    courseB.ratingValue = courseB.rating?.value || 0;
    value = sortByCategoryAndFilters(
      courseA,
      courseB,
      [...primarySortKeys, defaultSortKey],
      categoryFilter,
    );

    return value;
  });
};

export const applyFilters = ({ courses, similarCourses, sortBy }, isAnchored) => {
  const coursesList = isAnchored ? similarCourses : courses ?? [];
  const listToFilter = coursesList.filter((entity) => entity.type === 'Course');

  // Trick to keep the Deals always at the default interleaved place.
  const listOfDeals = coursesList.filter((entity) => entity.type === 'Deal');

  const listOfBanners = coursesList.filter((entity) => entity.type === 'Banner');

  let coursesSorted = [];
  let sortedTopTierCourses = [];

  const coursesWithTeeTimes = listToFilter
    .filter((item) => !!item.course?.availability?.forDate);
  const silverTierCourses = listToFilter
    .filter((item) => item.course?.partnerTier === 'silver');
  const coursesWithBookingSiteUrl = listToFilter
    .filter((item) => !!item.course?.bookingSiteUrl && !item.course?.availability?.forDate && item.course?.partnerTier !== 'silver');
  const coursesWithoutBookingSiteUrl = listToFilter
    .filter((item) => !item?.course?.bookingSiteUrl && !item.course?.availability?.forDate && item.course?.partnerTier !== 'silver');

  if (sortBy === 'price-lowest-first') {
    sortedTopTierCourses = [...sortCourses(coursesWithTeeTimes, sortBy), ...sortCourses(silverTierCourses, 'distance')];
  } else {
    sortedTopTierCourses = sortCourses([...coursesWithTeeTimes, ...silverTierCourses], sortBy);
  }

  coursesSorted = [
    ...sortedTopTierCourses,
    ...sortCourses(coursesWithBookingSiteUrl, sortBy === 'price-lowest-first' ? 'distance' : sortBy),
    ...sortCourses(coursesWithoutBookingSiteUrl, sortBy === 'price-lowest-first' ? 'distance' : sortBy),
  ];

  if (features.membership) {
    listOfBanners.forEach((banner, idx) => {
      const indexToInsert = DEFAULT_BANNER_GROUP * idx;

      coursesSorted.splice(indexToInsert, 0, banner);
    });
  }

  listOfDeals.forEach((deal, idx) => {
    const indexToInsert = DEFAULT_INTERCALATION_ROWS + idx * (DEFAULT_INTERCALATION_ROWS + 1);
    coursesSorted.splice(indexToInsert, 0, deal);
  });

  return coursesSorted;
};

const getCourses = (state) => state.merge({
  isLoading: true,
  isDone: false,
  isError: false,
  courseSection: null,
});

const getCoursesDone = (state, { courses, filterContent }) => state.merge({
  isLoading: false,
  isDone: true,
  isError: false,
  courses,
  filterContent,
});

const getCoursesError = (state, { message }) => state.merge({
  isLoading: false,
  isDone: true,
  isError: true,
  errorMessage: message,
});

const getSimilarCourses = (state) => state.merge({
  isLoading: true,
  isDone: false,
  isError: false,
  courseSection: null,
});

const getSimilarCoursesDone = (state, { course, similarCourses }) => state.merge({
  isLoading: false,
  isDone: true,
  isError: false,
  course,
  similarCourses,
});

const getSimilarCoursesError = (state, { message }) => state.merge({
  isLoading: false,
  isDone: true,
  isError: true,
  errorMessage: message,
});

const getNextPage = (state) => state.merge({
  pageNumber: state.pageNumber + 1,
  hasMore: (state.pageNumber * constants.TIME_SLOT_PAGINATION < (
    state.course ? state.similarCourses.length : state.courses.length
  )),
});

const getSectionCourses = (state) => state.merge({
  isLoading: true,
  isDone: false,
  isError: false,
});

const getSectionCoursesDone = (state, { courseSection, courses }) => state.merge({
  isLoading: false,
  isDone: true,
  isError: false,
  courses,
  courseSection,
});

const getSectionCoursesError = (state, { message }) => state.merge({
  isLoading: false,
  isDone: true,
  isError: true,
  errorMessage: message,
});

const toggleCourseFavorite = (state, { favoriteCourseId }) => {
  if (state.course && state.course.id === favoriteCourseId) {
    return state.merge({ course: { ...state.course, isFavorite: !state.course.isFavorite } });
  }
  const coursesList = JSON.parse(
    JSON.stringify(state.course ? state.similarCourses : state.courses),
  );
  const course = coursesList.find(
    (c) => c.type === 'Course' && c.course.id === favoriteCourseId,
  );

  course.course.isFavorite = !course.course.isFavorite;

  return state.merge(state.course ? { similarCourses: coursesList } : { courses: coursesList });
};

const setViewMode = (state, { viewMode }) => state.merge({
  viewMode,
});

const setCourseBanners = (state, { banners }) => state.merge({
  banners,
});

export const getPaginatedListSelector = (state, isAnchored) => applyFilters(
  state,
  isAnchored,
).slice(0, state.pageNumber * constants.TIME_SLOT_PAGINATION);

export const getListLengthSelector = (state, isAnchored) => applyFilters(state, isAnchored).length;

export const reducer = createReducer(INITIAL_STATE, {
  [Types.GET_COURSES]: getCourses,
  [Types.GET_COURSES_DONE]: getCoursesDone,
  [Types.GET_COURSES_ERROR]: getCoursesError,
  [Types.GET_SIMILAR_COURSES]: getSimilarCourses,
  [Types.GET_SIMILAR_COURSES_DONE]: getSimilarCoursesDone,
  [Types.GET_SIMILAR_COURSES_ERROR]: getSimilarCoursesError,
  [Types.GET_NEXT_PAGE]: getNextPage,
  [Types.GET_SECTION_COURSES]: getSectionCourses,
  [Types.GET_SECTION_COURSES_DONE]: getSectionCoursesDone,
  [Types.GET_SECTION_COURSES_ERROR]: getSectionCoursesError,
  [Types.SET_VIEW_MODE]: setViewMode,
  [Types.SET_COURSE_BANNERS]: setCourseBanners,
  [Types.TOGGLE_COURSE_FAVORITE]: toggleCourseFavorite,
});
